import React from 'react';
import {
  AlertDialog,
  AlertDialogContent,
  AlertDialogHeader,
  AlertDialogFooter,
  AlertDialogTitle,
  AlertDialogDescription,
  AlertDialogCancel,
} from '@/components/ui/alert-dialog';
import { Button } from '@/components/ui/button';
import { Loader2 } from 'lucide-react';

/**
 * Reusable confirmation dialog to replace all native window.confirm() calls.
 *
 * Usage:
 *   const [confirmState, setConfirmState] = useState({ open: false });
 *   const openConfirm = (opts) => setConfirmState({ open: true, ...opts });
 *   const closeConfirm = () => setConfirmState({ open: false });
 *
 *   openConfirm({
 *     title: 'Delete item?',
 *     description: 'This action cannot be undone.',
 *     confirmText: 'Delete',
 *     variant: 'destructive',
 *     onConfirm: async () => { await deleteItem(); closeConfirm(); },
 *   });
 *
 *   <ConfirmDialog {...confirmState} onClose={closeConfirm} />
 */
export function ConfirmDialog({
  open = false,
  onClose,
  title = 'Are you sure?',
  description = '',
  confirmText = 'Confirm',
  cancelText = 'Cancel',
  variant = 'destructive',
  onConfirm,
  loading = false,
}) {
  const [isLoading, setIsLoading] = React.useState(false);

  const handleConfirm = async () => {
    if (!onConfirm) return;
    setIsLoading(true);
    try {
      await onConfirm();
    } finally {
      setIsLoading(false);
    }
  };

  const busy = loading || isLoading;

  return (
    <AlertDialog open={open} onOpenChange={(v) => !v && !busy && onClose?.()}>
      <AlertDialogContent data-testid="confirm-dialog">
        <AlertDialogHeader>
          <AlertDialogTitle data-testid="confirm-dialog-title">{title}</AlertDialogTitle>
          {description && (
            <AlertDialogDescription data-testid="confirm-dialog-description">
              {description}
            </AlertDialogDescription>
          )}
        </AlertDialogHeader>
        <AlertDialogFooter>
          <AlertDialogCancel disabled={busy} data-testid="confirm-dialog-cancel">
            {cancelText}
          </AlertDialogCancel>
          <Button
            variant={variant}
            onClick={handleConfirm}
            disabled={busy}
            data-testid="confirm-dialog-confirm"
          >
            {busy && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
            {confirmText}
          </Button>
        </AlertDialogFooter>
      </AlertDialogContent>
    </AlertDialog>
  );
}
